import { logD, logE, logI } from "@blacknut/logging/dist";
import {
  AppLayout,
  configureStore,
  getConfig,
  logout,
  setAppLayout,
  setAUDID,
  setDeeplink,
  setLocale,
  setReferrer,
  setInstallReferrer,
  setSupportedProtocols,
  setUpdateAvailable,
  StorageKey,
  syncTokens,
  DeprecatedStorageKey,
} from "core";
import {
  isTV,
  UserAgent,
  apiService,
  ClientDeviceType,
  GameStreamProtocol,
  leMagService,
  Token,
  UserToken,
  SupportedOS,
} from "@blacknut/javascript-sdk/dist";
import { CustomBackContextProvider, UXContextProvider } from "@blacknut/react-sdk/dist";
import { SpatialNavigationProvider } from "@blacknut/spatialnav-sdk/dist";
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { Provider, useDispatch } from "react-redux";
import AppRouter from "./appRouter";
import { PreviewContextProvider } from "./components/Modals/PreviewContext";
import { useDatadog } from "./datadog";
import i18n from "./i18n/i18n";
import electronService from "./services/ElectronService";
import { LocalStorageService } from "./services/LocalStorageService";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { ThemeContextProvider } from "./theme/ThemeProvider";
import { MuteVideoContextProvider } from "./utils/MuteVideoContext";
import { nativeBridge } from "./utils/NativeBridge";
import { OrientationContextProvider } from "./utils/OrientationContext";
import { PerformanceContextProvider } from "./utils/PerformanceContext/PerformanceContext";
import { TileWidthContextProvider } from "./utils/TileWidthProvider";
import { useLogging } from "./utils/useLogging";
import { LOGGING_TAG } from "./utils/Utils";

const matchLocale = (current: string, expected: string) => {
  const matchFull = current && current.match(/([a-zA-Z]{2})-[a-zA-Z]{2}/);
  const matchPartial = current && current.match(/([a-zA-Z]{2})/);
  return (
    (matchPartial && matchPartial[1].toLowerCase() == expected) ||
    (matchFull && matchFull[1].toLowerCase()) === expected
  );
};
const storageService = new LocalStorageService();
const qs = new URLSearchParams(window.location.search);

let localeFromLocalStorage = localStorage.getItem(StorageKey.LOCALE);
if (localeFromLocalStorage) {
  localeFromLocalStorage = JSON.parse(localeFromLocalStorage);
}
console.debug("locale from local storage %o", localeFromLocalStorage);

let apiPathFromOrigin: string | undefined = undefined;
if (window.origin && (window.origin.match(/dev/) || window.origin.match(/localhost/))) {
  apiPathFromOrigin = "https://dev-api.blacknut.net/api";
} else if (window.origin && window.origin.match(/staging/)) {
  apiPathFromOrigin = "https://staging-api.blacknut.net/api";
}
logI(LOGGING_TAG, "api path from origin: %o", apiPathFromOrigin);

let apiPathFromUrlParams: string | undefined = undefined;
if (qs.get("env")?.match(/dev/)) {
  apiPathFromUrlParams = "https://dev-api.blacknut.net/api";
} else if (qs.get("env")?.match(/staging/)) {
  apiPathFromUrlParams = "https://staging-api.blacknut.net/api";
} else if (qs.get("env")?.match(/prod/)) {
  apiPathFromUrlParams = "https://api.blacknut.com/api";
}
logI(LOGGING_TAG, "api path from url params: %o", apiPathFromUrlParams);

let apiPath =
  apiPathFromUrlParams || apiPathFromOrigin || "https://api.blacknut.com/api";
let tokenFromCommandLine: Token | undefined;
let localeFromCmdLine: string | undefined;
let deeplinks: string[] | undefined;
const accessTokenFromQuery = qs.get("at");
const refreshTokenFromQuery = qs.get("rt");
const audidFromQuery = qs.get("audid");
const isNewUserFromQuery = qs.get("isNewUser");

if (
  qs.get("option") === "legacy" ||
  // https://blacknut.atlassian.net/browse/CA-5159
  // Add legacy mode to Fire OS 5, which is based on Android 5.1 (Lollipop, API level 22) --> https://developer.amazon.com/docs/fire-tv/fire-os-overview.html#:~:text=Fire%20OS%205%3A%20Based%20on,(Lollipop%2C%20API%20level%2022)
  (apiService.userAgent.os === SupportedOS.Fireos &&
    apiService.userAgent.androidVersion === "22")
) {
  localStorage.setItem(StorageKey.TV_LEGACY, "true");
}

if (qs.get("option") === "legacyNoTrailer") {
  localStorage.setItem(StorageKey.TV_LEGACY, "true");
  localStorage.setItem(StorageKey.TV_LEGACY_NO_TRAILER, "true");
}

const isTVLegacy = localStorage.getItem(StorageKey.TV_LEGACY) === "true";

// Dirty fix on TV_VIRTUAL_KEYBOARD. was set to true for a while in local storage
localStorage.removeItem(DeprecatedStorageKey.TV_VIRTUAL_KEYBOARD);

if (electronService.isAvailable()) {
  const args: string[] = electronService.getCommandLineArgs();
  console.debug("Command line args %o", args);
  if (args) {
    for (let i = 0; i < args.length; i++) {
      if (args[i] === "--apiPath" && i < args.length - 1) {
        apiPath = args[i + 1];
      }
      if (args[i].startsWith("--token=")) {
        /* windows*/
        tokenFromCommandLine = JSON.parse(args[i].slice(8));
      } else if (args[i] === "--token" && i < args.length - 1) {
        tokenFromCommandLine = JSON.parse(args[i + 1]);
      } else if (args[i] === "--locale" && i < args.length - 1) {
        localeFromCmdLine = args[i + 1];
      }
    }
  }
  deeplinks = args.filter((s) =>
    s.startsWith(`${process.env.REACT_APP_DEEPLINK_SCHEME}://`),
  );
  console.log("deeplinks: ", deeplinks);
}
console.debug("locale from command line:", localeFromCmdLine);
console.debug("tokenFromCommandLine %o", tokenFromCommandLine);

localStorage.setItem(StorageKey.INSTALL_NOTIF, "true");
if (isNewUserFromQuery) localStorage.setItem(StorageKey.NEW_USER, "true");

/*****************
 * Recovery from <= electron v3
 * Retrieves token, profile data & theme from file:// local storage to current one
 */
if (electronService.isAvailable()) {
  const opts = electronService.recoverFromV3();
  if (opts.token) {
    localStorage.setItem(StorageKey.TOKEN, opts.token);
  }
  if (opts.profilesData) {
    localStorage.setItem(StorageKey.PROFILES_DATA, opts.profilesData);
  }
  if (opts.theme) {
    localStorage.setItem(StorageKey.THEME_BW, opts.theme);
  }
}

let token: UserToken | undefined = undefined;
///////////////
// Token init
// First use query params
// Then use electron command line
///////////////
if (accessTokenFromQuery && refreshTokenFromQuery) {
  const uat = qs.get("uat");
  const urt = qs.get("urt");
  token = {
    familyToken: {
      accessToken: accessTokenFromQuery,
      refreshToken: refreshTokenFromQuery,
    },
    userToken:
      uat && urt
        ? {
            accessToken: uat,
            refreshToken: urt,
          }
        : undefined,
  };
}

if (electronService.isAvailable() && tokenFromCommandLine && !token) {
  logD(LOGGING_TAG, "Got token from electron %o", tokenFromCommandLine);
  tokenFromCommandLine = token;
}

if (token) {
  storageService
    .setItem(StorageKey.TOKEN, token)
    .then(() => console.log("persist OK"))
    .catch((e) => {
      console.error("Caught error persiting token", e);
    });
}

const store = configureStore(storageService, {
  apiPath,
  protocole:
    isTV() || !electronService.isAvailable()
      ? GameStreamProtocol.WEBRTC
      : GameStreamProtocol.AUTO,
  apiVersion: "5.0.4",
  allowAnonymous: !!process.env.REACT_APP_ALLOW_ANONYMOUS,
  startUrl: window.location.origin,
});
store.dispatch(setSupportedProtocols([GameStreamProtocol.WEBRTC]));

if (deeplinks && deeplinks.length > 0) {
  setDeeplink(deeplinks[0])(store.dispatch);
}

if (audidFromQuery) {
  setAUDID(audidFromQuery)(store.dispatch);
}

if (localeFromCmdLine) {
  setLocale(localeFromCmdLine)(store.dispatch);
}

const locale =
  localeFromCmdLine ||
  localeFromLocalStorage ||
  navigator.language ||
  navigator["userLanguage"];
if (matchLocale(locale, "fr")) {
  leMagService.locale = "fr";
} else if (matchLocale(locale, "es")) {
  leMagService.locale = "es";
} else {
  leMagService.locale = "en";
}

const Datadog = () => {
  useDatadog();
  return null;
};

const Logger = () => {
  useLogging();
  return null;
};

const TabAwareTokenListener = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (event.key === StorageKey.TOKEN) {
        logD(LOGGING_TAG, "TOKEN CHANGED : %o", event.newValue);
        try {
          if (event.newValue) {
            const oTokens = JSON.parse(event.newValue);
            if (event.storageArea === localStorage) {
              dispatch(syncTokens(oTokens));
            }
          } else {
            logout()(store.dispatch);
          }
        } catch (e) {
          logE(LOGGING_TAG, "Caugth error parsing token", e);
        }
      }
    };
    window.addEventListener("storage", listener);
    return () => {
      window.removeEventListener("storage", listener);
    };
  }, [dispatch]);
  return null;
};

const App = () => {
  const [inited, setInited] = useState(false);
  const [removeOnPressEnter, setRemoveOnPressEnter] = useState(false);
  React.useEffect(() => {
    const init = async () => {
      try {
        await nativeBridge.init();
      } catch (e) {
        logE(LOGGING_TAG, "Caught error init native bridge", e);
      }

      let deviceInfo: UserAgent = { ...apiService.userAgent };
      if (nativeBridge.module?.getDeviceInfo) {
        try {
          deviceInfo = await nativeBridge.module?.getDeviceInfo();
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting device info", e);
        }
      }
      if (deviceInfo) {
        apiService.userAgent = {
          ...apiService.userAgent,
          ...deviceInfo,
          apiVersion: apiService.userAgent.apiVersion,
        };
      }
      if (apiService.userAgent.client === ClientDeviceType.PHONE) {
        store.dispatch(setAppLayout(AppLayout.PHONE));
      } else if (apiService.userAgent.client == ClientDeviceType.TABLET) {
        store.dispatch(setAppLayout(AppLayout.TABLET));
      } else if (apiService.userAgent.client == ClientDeviceType.TV) {
        store.dispatch(setAppLayout(AppLayout.TV));
        document.body.classList.add("lowend");
      }
      document.body.setAttribute("data-layout", apiService.userAgent.client);

      if (nativeBridge.module?.checkForUpdates) {
        try {
          await nativeBridge.module?.checkForUpdates();
        } catch (e) {
          logE(LOGGING_TAG, "Caught error checking for updates", e);
        }
      }

      if (nativeBridge.module?.getReferrer) {
        try {
          const referrer = await nativeBridge.module?.getReferrer();
          setReferrer(referrer)(store.dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting referrer", e);
        }
      }

      if (nativeBridge.module?.getInstallReferrer) {
        try {
          const installReferrer = await nativeBridge.module?.getInstallReferrer();
          setInstallReferrer(installReferrer)(store.dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting installReferrer", e);
        }
      }

      ReactModal.setAppElement("#root");

      // could be done in App.router but in order to be coherent with the android part
      // do this here
      const savedAPI = localStorage.getItem(StorageKey.PLATFORM);
      apiService.endpoint = savedAPI ? JSON.parse(savedAPI) : apiPath;

      // Need to be set before getting config to get feature flip: https://blacknut.atlassian.net/browse/CA-6298
      if (token?.familyToken && !apiService.familyToken) {
        apiService.familyToken = { ...token?.familyToken };
      }
      try {
        const config = await getConfig()(store.dispatch);
        if (
          apiService.userAgent.os === SupportedOS.Webos &&
          (config.lgTryBuyAllowed || qs.get("lg10years") === "2")
        ) {
          localStorage.setItem(StorageKey.FEAT_WEBOS10YRS, "true");
        } else {
          localStorage.removeItem(StorageKey.FEAT_WEBOS10YRS);
        }
        if (config.blacknutTryBuy || qs.get("trybuy") === "1") {
          localStorage.setItem(StorageKey.FEAT_TRYBUY, "true");
        } else {
          localStorage.removeItem(StorageKey.FEAT_TRYBUY);
        }
        if (config.deviceKind !== apiService.userAgent.client) {
          switch (config.deviceKind) {
            case ClientDeviceType.DESKTOP:
              apiService.userAgent = {
                ...apiService.userAgent,
                client: ClientDeviceType.DESKTOP,
                deviceType: "DESKTOP",
              };
              break;
            case ClientDeviceType.TABLET:
              apiService.userAgent = {
                ...apiService.userAgent,
                client: ClientDeviceType.TABLET,
                deviceType: "TABLET",
              };
              break;
            case ClientDeviceType.PHONE:
              apiService.userAgent = {
                ...apiService.userAgent,
                client: ClientDeviceType.PHONE,
                deviceType: "MOBILE",
              };
              break;
            case ClientDeviceType.TV:
              apiService.userAgent = {
                ...apiService.userAgent,
                client: ClientDeviceType.TV,
                deviceType: "TV",
              };
              break;
          }
          logD(LOGGING_TAG, "Overriding device kind from server: %o", config.deviceKind);
        }
      } catch (e) {
        logE(LOGGING_TAG, "Caught error getting config");
      }

      try {
        if (nativeBridge.module?.removeOnPressEnter) {
          setRemoveOnPressEnter(await nativeBridge.module?.removeOnPressEnter?.());
        }
      } catch (e) {
        logE(LOGGING_TAG, "Error getting removeOnPressEnter  feat %o", e);
      }

      if (apiService.userAgent.client === ClientDeviceType.TV) {
        document.documentElement.style.fontSize = "14px";

        document
          .querySelector("meta[name=viewport]")
          ?.setAttribute(
            "content",
            "width=device-width, initial-scale=" +
              window.innerWidth / 1920 +
              ", maximum-scale=1.0, user-scalable=0",
          );

        if (window.innerWidth !== 1920) {
          // set root element fontSize
          // because the meta viewport has no effect
          document.documentElement.style.fontSize =
            (window.innerWidth / 1920) * 14 + "px";
        }
      }

      setInited(true);
    };
    init();
  }, []);

  return (
    <PerformanceContextProvider>
      <Provider store={store}>
        <Datadog />
        <Logger />
        <TabAwareTokenListener />
        <ThemeContextProvider>
          <PreviewContextProvider>
            <OrientationContextProvider>
              <MuteVideoContextProvider>
                {inited && (
                  <>
                    <TileWidthContextProvider>
                      <CustomBackContextProvider>
                        <SpatialNavigationProvider
                          debug
                          defaultEnabled={
                            apiService.userAgent.client == ClientDeviceType.TV
                          }
                          removeOnPressEnter={removeOnPressEnter}
                        >
                          <UXContextProvider useRipple={!isTVLegacy}>
                            <AppRouter tokenFromCommandLine={!!token?.userToken} />
                          </UXContextProvider>
                        </SpatialNavigationProvider>
                      </CustomBackContextProvider>
                    </TileWidthContextProvider>
                  </>
                )}
              </MuteVideoContextProvider>
            </OrientationContextProvider>
          </PreviewContextProvider>
        </ThemeContextProvider>
      </Provider>
    </PerformanceContextProvider>
  );
};
export default App;

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
if (electronService.isAvailable()) {
  serviceWorkerRegistration.unregister();
} else {
  serviceWorkerRegistration.register({
    onSuccess: () => {
      console.log("onSuccess");
    },
    onUpdate: (registration) => {
      registration.unregister().then(() => {
        setUpdateAvailable(true)(store.dispatch);
      });
    },
  });
}
